import { Fragment, memo } from 'react';

/**
 * Regex pattern to match URLs starting with http:// or https://
 * Captures URLs until whitespace or common delimiters are encountered
 * Note: This matches greedily; trailing punctuation is handled by cleanUrl()
 */
const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;

/**
 * Strip trailing punctuation that is commonly used in prose but not typically part of URLs.
 * Handles balanced parentheses (common in Wikipedia URLs) and common sentence-ending punctuation.
 *
 * @returns Object with cleaned URL and any stripped trailing punctuation
 */
function cleanUrl(url: string): { cleanedUrl: string; trailingPunctuation: string } {
  let cleaned = url;

  // Strip trailing punctuation characters that are almost never valid at the end of a URL
  // This handles cases like "Visit https://example.com." or "Check https://example.com!"
  while (/[.,;:!?]$/.test(cleaned)) {
    cleaned = cleaned.slice(0, -1);
  }

  // Handle unbalanced closing parentheses/brackets at the end
  // This correctly preserves balanced parens like in Wikipedia URLs: https://en.wikipedia.org/wiki/Foo_(bar)
  while (/[)\]]$/.test(cleaned)) {
    const lastChar = cleaned.slice(-1);
    const openChar = lastChar === ')' ? '(' : '[';
    const openRegex = new RegExp(`\\${openChar}`, 'g');
    const closeRegex = new RegExp(`\\${lastChar === ')' ? ')' : ']'}`, 'g');
    const openCount = (cleaned.match(openRegex) || []).length;
    const closeCount = (cleaned.match(closeRegex) || []).length;

    if (closeCount > openCount) {
      cleaned = cleaned.slice(0, -1);
      // After removing a bracket, check for more trailing punctuation
      while (/[.,;:!?]$/.test(cleaned)) {
        cleaned = cleaned.slice(0, -1);
      }
    } else {
      break;
    }
  }

  return {
    cleanedUrl: cleaned,
    trailingPunctuation: url.slice(cleaned.length),
  };
}

interface LinkifyProps {
  children: string;
}

/**
 * Component that detects URLs in text and renders them as clickable hyperlinks.
 * URLs starting with http:// or https:// are converted to anchor tags that open in a new tab.
 *
 * Features:
 * - Automatically strips trailing punctuation (., !, ?, etc.) from URLs
 * - Handles balanced parentheses in URLs (e.g., Wikipedia links)
 * - Memoized to prevent unnecessary re-renders
 *
 * @example
 * ```tsx
 * <Linkify>Check out https://example.com for more info</Linkify>
 * // Renders: Check out <a href="https://example.com" target="_blank">https://example.com</a> for more info
 *
 * <Linkify>Visit https://example.com.</Linkify>
 * // Renders: Visit <a href="https://example.com">https://example.com</a>.
 * ```
 */
export const Linkify = memo(function Linkify({ children }: LinkifyProps) {
  if (!children || typeof children !== 'string') {
    return <>{children}</>;
  }

  // Find all URL matches with their positions
  const matches = [...children.matchAll(URL_REGEX)];

  // Early return for plain text (no URLs found)
  if (matches.length === 0) {
    return <>{children}</>;
  }

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  matches.forEach((match, i) => {
    const rawUrl = match[0];
    const startIndex = match.index!;
    const { cleanedUrl, trailingPunctuation } = cleanUrl(rawUrl);

    // Add text before the URL
    if (startIndex > lastIndex) {
      result.push(<Fragment key={`text-${i}`}>{children.slice(lastIndex, startIndex)}</Fragment>);
    }

    // Add the link with cleaned URL
    result.push(
      <a
        key={`link-${i}`}
        href={cleanedUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        {cleanedUrl}
      </a>
    );

    // Add any stripped punctuation as plain text after the link
    if (trailingPunctuation) {
      result.push(<Fragment key={`punct-${i}`}>{trailingPunctuation}</Fragment>);
    }

    lastIndex = startIndex + rawUrl.length;
  });

  // Add any remaining text after the last URL
  if (lastIndex < children.length) {
    result.push(<Fragment key="text-end">{children.slice(lastIndex)}</Fragment>);
  }

  return <>{result}</>;
});
